import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Link,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/core';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import TwitterIcon from '../../../assets/twitter_bird.svg';
import WalletUnlockModal from '../../../components/WalletUnlockModal';
import { getClient } from '../../../services/chainClient';
import { getTowerService } from '../../../services/towerService';
import { RootState } from '../../../store/reducers';
import { popupCenter } from '../../../utils/Utils';

interface SetupInfluencerVerifyProps {
  twitterInfo: any;
  pubkey: string;
  onNext: () => void;
  onBack?: () => void;
}

const SetupInfluencerVerify = ({
  twitterInfo,
  pubkey,
  onNext,
  onBack,
}: SetupInfluencerVerifyProps) => {
  const mnemonic = useSelector((state: RootState) => state.wallet.mnemonic);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(false);

  const handleTwitterVerify = async () => {
    setVerifying(true);
    setVerifyError(false);

    const tower = getTowerService(getClient());
    await tower.initialize();

    try {
      const {
        data: { ok },
      } = await tower.postTwitterVerification(twitterInfo.id_str);

      if (!ok) {
        setVerifyError(true);
        setVerifying(false);
      } else {
        localStorage.setItem('twitterInfo', JSON.stringify(twitterInfo));
        onNext();
      }
    } catch (e) {
      setVerifying(false);
      setVerifyError(true);
      throw e;
    }
  };

  const tweet = `Verifying myself on @AtomicFinance! Public key: ${pubkey}
https://atomic.finance/odds/app/offers/${twitterInfo.screen_name}`;

  return (
    <>
      {!mnemonic && <WalletUnlockModal />}
      <Flex flexDir="column">
        <Flex
          flexDir="column"
          bg="#1da1f2"
          alignItems="center"
          minH="200px"
          flex={1}
          borderRadius={4}
        >
          <Text color="white" my={2}>
            Your Profile
          </Text>
          <Flex display="flex" alignItems="center" w="330px" h="140px">
            <Flex
              bg="white"
              w="full"
              h="full"
              p={4}
              borderRadius={8}
              alignItems="center"
              justifyContent="center"
            >
              <VStack alignItems="center" spacing={2} w="inherit">
                <>
                  <Box
                    borderRadius={100}
                    boxSize="50px"
                    bg="#EBEBEB"
                    background={`url(${twitterInfo.profile_image_url_https.replace(
                      'normal',
                      '200x200'
                    )})`}
                    backgroundSize="cover"
                  ></Box>

                  <Text
                    w="80%"
                    textAlign="center"
                    fontWeight="bold"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    {twitterInfo.name}
                  </Text>
                  <Text
                    w="100%"
                    textAlign="center"
                    fontSize="xs"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    textOverflow="ellipsis"
                  >
                    {twitterInfo.description}
                  </Text>
                </>
              </VStack>
            </Flex>
          </Flex>
        </Flex>
        <Flex flex={1} flexDir="column">
          <Flex
            display="flex"
            alignItems="center"
            flexDir="column"
            justifyContent="center"
            mt={4}
          >
            <Heading as="h4" size="sm">
              Verify your Twitter handle
            </Heading>
            <Text fontSize="sm">
              We do this to ensure that you are indeed the owner of your Twitter
              account.
            </Text>
          </Flex>

          <Flex
            justifyContent="center"
            alignItems="center"
            flex={0.6}
            flexDir="column"
          >
            <Textarea
              borderColor="gray.800"
              isReadOnly
              resize="none"
              w="60%"
              my={4}
              value={tweet}
            />

            <Link
              onClick={() =>
                popupCenter({
                  url: `https://twitter.com/intent/tweet?url=https%3A%2F%2Fatomic.finance%2Fodds%2Fapp%2Finfluencer%2F${twitterInfo.screen_name}&text=Verifying%20myself%20on%20@AtomicFinance%21%20Public%20key%3A%20${pubkey}`,
                  title: 'Verify Twitter Handle',
                  w: 500,
                  h: 500,
                  opts: {
                    toolbar: 1,
                    resizable: 1,
                    location: 1,
                    menubar: 1,
                    status: 1,
                  },
                })
              }
              target="_blank"
              position="relative"
              height="28px"
              fontSize="12px"
              padding="4px 8px 4px 6px"
              backgroundColor="#1b95e0"
              color="#fff"
              alignItems="center"
              borderRadius="3px"
              display="inline-flex"
              fontWeight="500"
              mb={2}
              width="60%"
              justifyContent="center"
              cursor="pointer"
            >
              <Image
                src={TwitterIcon}
                display="inherit"
                height="12px"
                mr="1px"
              />
              Tweet above message to verify your Twitter handle
            </Link>
            {verifyError && (
              <Text
                fontSize="xs"
                mt={2}
                textAlign="center"
                fontWeight={500}
                color="red.400"
              >
                Failed to verify. Tweet proof not found.
              </Text>
            )}
          </Flex>

          <Flex flexDir="row" m={4}>
            {onBack && (
              <Button leftIcon={<ChevronLeftIcon />} onClick={onBack}>
                Go Back
              </Button>
            )}

            <Button
              alignSelf="flex-end"
              colorScheme="blue"
              rightIcon={<ChevronRightIcon />}
              onClick={handleTwitterVerify}
              isLoading={verifying}
              ml="auto"
            >
              Complete Verification
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default SetupInfluencerVerify;
